import { Injectable } from '@angular/core';
import { Device } from '@ionic-native/device';
import { Geolocation } from '@ionic-native/geolocation';
import { Globalization } from '@ionic-native/globalization';
import { APP_CONFIG, Env } from '../../environment';
import { Platform } from 'ionic-angular';
import { IDeviceInfo } from '../../model/models/device/device-info.interface';
import { IGeolocation } from '../../model/models/device/geolocation.interface';
import { LoggerEvent, LoggerProvider } from '../logger/logger';
import { ForeignIpGeolocationTestOverrides } from '../foreign-ip-geolocation/foreign-ip-geolocation-test-overrides';
import { GEOLOCATION_FIX_REUSE_MS, isValidCoordinatePair } from '../foreign-ip-geolocation/foreign-ip-geolocation-session';
import { IRmtLocationFix, IRmtLocationPlugin } from '../../model/models/device/rmt-location-plugin.interface';

/**
 * @description Device id provider, handle to store device id in local storage
 */
@Injectable()
export class DeviceProvider {

  constructor(
    private device: Device,
    private geolocation: Geolocation,
    private globalization: Globalization,
    private platform: Platform) {
  }

  static getDeviceTimeZone(): string | null {
    const override = ForeignIpGeolocationTestOverrides.timeZone();
    if (override !== undefined) {
      return override;
    }
    return DeviceProvider.getIntlTimeZone();
  }

  static getIntlTimeZone(): string | null {
    try {
      const intl: any = (typeof Intl !== 'undefined') ? Intl : null;
      if (!intl || typeof intl.DateTimeFormat !== 'function') {
        return null;
      }
      const resolved = intl.DateTimeFormat().resolvedOptions();
      if (!resolved || !resolved.timeZone) {
        return null;
      }
      const timeZone = String(resolved.timeZone);
      return timeZone.trim() ? timeZone : null;
    } catch (ex) {
      return null;
    }
  }

  getDeviceTimeZone(): string | null {
    return DeviceProvider.getDeviceTimeZone();
  }

  async getDeviceInfo(): Promise<IDeviceInfo> {
    if (this.platform.is('cordova')) {
      const locale = await this.globalization.getLocaleName();
      return {
        id: this.device.uuid,
        cordovaVersion: this.device.cordova,
        model: this.device.model,
        platform: this.device.platform,
        uuid: this.device.uuid,
        operatingSystemVersion: this.device.version,
        manufacturer: this.device.manufacturer,
        isVirtual: this.device.isVirtual,
        hardwareSerialNumber: this.device.serial,
        locale: (locale) ? locale.value : '',
        ...((APP_CONFIG.env !== Env.PROD) ? { EnforcedRefresh: APP_CONFIG.enforceRefresh } : {})
      };
    }

    return {
      id: 'DEVICEID',
      cordovaVersion: 'NOT-CORDOVA',
      model: 'NOT-CORDOVA',
      platform: 'NOT-CORDOVA',
      uuid: 'NOT-CORDOVA',
      operatingSystemVersion: 'NOT-CORDOVA',
      manufacturer: 'NOT-CORDOVA',
      isVirtual: true,
      hardwareSerialNumber: 'NOT-CORDOVA',
      locale: 'NOT-CORDOVA',
      ...((APP_CONFIG.env !== Env.PROD) ? { EnforcedRefresh: APP_CONFIG.enforceRefresh } : {})
    };
  }

  async getGeolocation(): Promise<IGeolocation> {
    const override = ForeignIpGeolocationTestOverrides.fix();
    if (override !== undefined) {
      return override;
    }
    const rmtLocation = this.getRmtLocationPlugin();
    if (this.platform.is('cordova') && rmtLocation) {
      return this.getGeolocationFromPlugin(rmtLocation);
    }
    return this.getGeolocationFromIonicNative();
  }

  private async getGeolocationFromPlugin(rmtLocation: IRmtLocationPlugin): Promise<IGeolocation> {
    const startedAt = Date.now();
    return new Promise<IGeolocation>((res) => {
      try {
        rmtLocation.getCurrentPosition({ timeoutMs: APP_CONFIG.timeoutGeolocation, maxLastKnownAgeMs: GEOLOCATION_FIX_REUSE_MS }, (fix: IRmtLocationFix) => {
          LoggerProvider.info(this, `rmt-location fix in ${Date.now() - startedAt} ms`);
          res(DeviceProvider.toGeolocation(fix));
        }, (reason) => {
          LoggerProvider.warning(this, LoggerEvent.CordovaGeolocation, `rmt-location getCurrentPosition failed after ${Date.now() - startedAt} ms: ${reason}`);
          res(null);
        });
      } catch (ex) {
        LoggerProvider.exception(this, ex, LoggerEvent.CordovaGeolocation, 'rmt-location getCurrentPosition threw');
        res(null);
      }
    });
  }

  private async getGeolocationFromIonicNative(): Promise<IGeolocation> {
    return new Promise<IGeolocation>((res) => {
      this.geolocation.getCurrentPosition({
        timeout: APP_CONFIG.timeoutGeolocation,
        enableHighAccuracy: true, // Apparently this is a magic flag to get Android to work.
      }).then((location) => res({
        latitude: location.coords.latitude,
        longitude: location.coords.longitude,
        accuracy: location.coords.accuracy,
        isMock: false,
        timestamp: location.timestamp || Date.now()
      })).catch((_err) => {
        res(null);
      });
    });
  }

  private static toGeolocation(fix: IRmtLocationFix): IGeolocation | null {
    if (!isValidCoordinatePair(fix)) {
      return null;
    }
    return {
      latitude: fix.latitude,
      longitude: fix.longitude,
      accuracy: (typeof fix.accuracy === 'number') ? fix.accuracy : undefined,
      isMock: !!fix.isMock,
      timestamp: fix.timestamp || Date.now()
    };
  }

  getRmtLocationPlugin(): IRmtLocationPlugin | null {
    const w: any = window;
    return (w && w.plugins && w.plugins.RmtLocation) ? w.plugins.RmtLocation as IRmtLocationPlugin : null;
  }
}



// WEBPACK FOOTER //
// ./src/providers/device/device.ts